import { api, OfflineError } from './api'
import { db, kvGet, kvSet, type Expense, type Order, type Shift } from './db'

/**
 * Đẩy đơn hàng và phiếu chi lên máy chủ.
 *
 * Máy khách là nguồn sự thật cho dữ liệu nó tạo ra. Id do máy khách sinh nên đẩy lại
 * nhiều lần vẫn ra cùng kết quả — mạng ở chợ rớt giữa chừng liên tục, gửi lại phải an toàn.
 * Không có mạng thì im lặng bỏ qua, lần sau đẩy tiếp; người bán không cần biết.
 */

const BATCH = 100

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error'

export interface SyncStatus {
  state: SyncState
  pending: number
  lastSyncAt: string | null
  /** Lần cuối kéo được dữ liệu của máy khác về. */
  lastPullAt: string | null
  message?: string
}

type Listener = (s: SyncStatus) => void

let status: SyncStatus = { state: 'idle', pending: 0, lastSyncAt: null, lastPullAt: null }
const listeners = new Set<Listener>()
let running = false
let timer: ReturnType<typeof setInterval> | null = null

function emit(patch: Partial<SyncStatus>) {
  status = { ...status, ...patch }
  for (const l of listeners) l(status)
}

export function onSync(fn: Listener): () => void {
  listeners.add(fn)
  fn(status)
  return () => listeners.delete(fn)
}

export const syncStatus = () => status

export async function pendingCount(): Promise<number> {
  const [o, e, s] = await Promise.all([
    db.orders.where('synced').equals(0).count(),
    db.expenses.where('synced').equals(0).count(),
    db.shifts.where('synced').equals(0).count(),
  ])
  return o + e + s
}

const toOrderPush = (o: Order) => ({
  id: o.id,
  code: o.code,
  tableId: o.tableId,
  tableName: o.tableName,
  status: o.status,
  openedAt: o.openedAt,
  paidAt: o.paidAt,
  subtotal: o.subtotal,
  discount: o.discount,
  total: o.total,
  paymentMethod: o.paymentMethod,
  amountGiven: o.amountGiven,
  changeGiven: o.changeGiven,
  customerName: o.customerName,
  note: o.note,
  deviceId: o.deviceId,
  clientCreatedAt: o.clientCreatedAt,
  voidedAt: o.voidedAt ?? null,
  voidReason: o.voidReason ?? null,
  items: o.items.map((i) => ({
    id: i.id,
    productId: i.productId,
    name: i.name,
    unitPrice: i.unitPrice,
    qtyMilli: i.qtyMilli,
    lineTotal: i.lineTotal,
    optionLabel: i.optionLabel,
    note: i.note,
  })),
})

const toExpensePush = (e: Expense) => ({
  id: e.id,
  categoryId: e.categoryId,
  categoryName: e.categoryName,
  emoji: e.emoji,
  amount: e.amount,
  note: e.note,
  photoUrl: e.photoUrl,
  spentAt: e.spentAt,
  deviceId: e.deviceId,
  paymentMethod: e.paymentMethod ?? 'Cash',
})

const toShiftPush = (s: Shift) => ({
  id: s.id,
  openedAt: s.openedAt,
  closedAt: s.closedAt,
  openingCash: s.openingCash,
  cashSales: s.cashSales,
  cashExpense: s.cashExpense,
  expectedCash: s.expectedCash,
  countedCash: s.countedCash,
  cashDiff: s.cashDiff,
  note: s.note,
  deviceId: s.deviceId,
})

export async function pushNow(): Promise<void> {
  if (running) return
  running = true

  try {
    const pending = await pendingCount()
    emit({ pending })
    if (pending === 0) {
      emit({ state: 'idle' })
      return
    }

    emit({ state: 'syncing' })

    const orders = await db.orders.where('synced').equals(0).limit(BATCH).toArray()
    const expenses = await db.expenses.where('synced').equals(0).limit(BATCH).toArray()
    const shifts = await db.shifts.where('synced').equals(0).limit(BATCH).toArray()

    const result = await api.post<{ ordersSaved: number; expensesSaved: number; rejected: string[] }>(
      '/api/sync/push',
      {
        orders: orders.map(toOrderPush),
        expenses: expenses.map(toExpensePush),
        shifts: shifts.map(toShiftPush),
      },
    )

    const rejected = new Set(result.rejected)

    // Đơn bị máy chủ từ chối thì để nguyên cờ chưa đồng bộ và báo lên,
    // không đánh dấu xong. Nuốt lỗi ở đây là mất tiền của quán mà không ai biết.
    await db.transaction('rw', [db.orders, db.expenses, db.shifts], async () => {
      for (const o of orders) if (!rejected.has(o.id)) await db.orders.update(o.id, { synced: 1 })
      for (const e of expenses) if (!rejected.has(e.id)) await db.expenses.update(e.id, { synced: 1 })
      for (const s of shifts) if (!rejected.has(s.id)) await db.shifts.update(s.id, { synced: 1 })
    })

    const left = await pendingCount()
    emit({
      state: rejected.size > 0 ? 'error' : 'idle',
      pending: left,
      lastSyncAt: new Date().toISOString(),
      message: rejected.size > 0 ? `${rejected.size} đơn máy chủ không nhận` : undefined,
    })

    // Còn tồn thì đẩy tiếp ngay, khỏi đợi hết chu kỳ.
    if (left > 0 && rejected.size === 0) {
      running = false
      await pushNow()
      return
    }
  } catch (err) {
    if (err instanceof OfflineError) {
      emit({ state: 'offline', pending: await pendingCount() })
    } else {
      emit({
        state: 'error',
        pending: await pendingCount(),
        message: err instanceof Error ? err.message : 'Đồng bộ lỗi',
      })
    }
  } finally {
    running = false
  }
}

interface PullResult {
  orders: (Omit<Order, 'synced'> & { items: Order['items'] })[]
  expenses: Omit<Expense, 'synced'>[]
  shifts: Omit<Shift, 'synced'>[]
  fullAccess: boolean
  serverTime: string
}

/**
 * Kéo về những gì máy khác trong quán đã đẩy lên.
 *
 * Không có bước này thì ba thứ đều sai: hai máy không thấy bàn của nhau (hai người cùng
 * ghi đơn một bàn), ca làm mỗi máy một cái (số tiền phải có trong két tính sai), và chủ ở
 * nhà mở app ra chỉ thấy đơn của chính máy mình.
 *
 * Máy chủ tự cắt theo vai trò: nhân viên chỉ nhận đơn đang mở và ca đang mở.
 */
export async function pullNow(): Promise<void> {
  const since = (await kvGet<string>('lastPullAt')) ?? startOfToday()

  let data: PullResult
  try {
    data = await api.get<PullResult>(`/api/sync/pull?since=${encodeURIComponent(since)}`)
  } catch (err) {
    if (!(err instanceof OfflineError)) {
      emit({ message: err instanceof Error ? err.message : 'Không kéo được dữ liệu' })
    }
    return
  }

  await db.transaction('rw', [db.orders, db.expenses, db.shifts], async () => {
    for (const row of data.orders) {
      const mine = await db.orders.get(row.id)

      // Bản trên máy chưa đẩy lên thì nó mới hơn bản máy chủ — không đè lên.
      // Đè là mất đơn vừa ghi lúc mất mạng.
      if (mine?.synced === 0) continue

      await db.orders.put({ ...row, synced: 1 })
    }

    for (const row of data.expenses) {
      const mine = await db.expenses.get(row.id)
      if (mine?.synced === 0) continue
      await db.expenses.put({ ...row, synced: 1 })
    }

    for (const row of data.shifts) {
      const mine = await db.shifts.get(row.id)
      if (mine?.synced === 0) continue
      await db.shifts.put({ ...row, synced: 1 })
    }
  })

  // Lùi lại 30 giây: đồng hồ máy chủ và máy khách lệch nhau vài giây là đủ để bỏ sót
  // một bản ghi vừa được cập nhật ngay tại mốc đó.
  const next = new Date(new Date(data.serverTime).getTime() - 30_000).toISOString()
  await kvSet('lastPullAt', next)

  emit({ lastPullAt: data.serverTime })
}

function startOfToday(): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

/** Đẩy mỗi 30 giây, và đẩy ngay khi máy có mạng lại. */
export function startSync(intervalMs = 30_000): () => void {
  const kick = () => {
    // Đẩy trước rồi mới kéo: đơn vừa bán ở máy này lên trước, sau đó mới nhận về của
    // máy khác. Ngược lại thì bản vừa kéo về có thể đè lên đơn chưa kịp đẩy.
    void pushNow().then(pullNow)
  }

  timer = setInterval(kick, intervalMs)
  window.addEventListener('online', kick)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') kick()
  })
  kick()

  return () => {
    if (timer) clearInterval(timer)
    window.removeEventListener('online', kick)
  }
}
